import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/Input/Input';
import { cn } from '@/lib/utils';
import type { SettingsPanelProps } from './types';
import { CURRENCY_INFO, type SupportedCurrency } from './types';

export function SettingsPanel({ settings, eventCurrency, onChange }: SettingsPanelProps) {
  const currency = eventCurrency as SupportedCurrency;
  const currencyInfo = CURRENCY_INFO[currency] ?? CURRENCY_INFO.USD;

  // Local state for controlled inputs
  const [localValues, setLocalValues] = useState({
    contingency_percent: settings.contingency_percent.toString(),
    contingency_fixed: settings.contingency_fixed
      ? (settings.contingency_fixed / 100).toString()
      : '',
    planning_overhead_percent: settings.planning_overhead_percent.toString(),
    planning_overhead_fixed: settings.planning_overhead_fixed
      ? (settings.planning_overhead_fixed / 100).toString()
      : '',
    profit_margin_percent: settings.profit_margin_percent.toString(),
    profit_margin_fixed: settings.profit_margin_fixed
      ? (settings.profit_margin_fixed / 100).toString()
      : '',
  });

  // Track which mode (percent vs fixed) is active
  const [contingencyMode, setContingencyMode] = useState<'percent' | 'fixed'>(
    settings.contingency_fixed !== null ? 'fixed' : 'percent'
  );
  const [overheadMode, setOverheadMode] = useState<'percent' | 'fixed'>(
    settings.planning_overhead_fixed !== null ? 'fixed' : 'percent'
  );
  const [marginMode, setMarginMode] = useState<'percent' | 'fixed'>(
    settings.profit_margin_fixed !== null ? 'fixed' : 'percent'
  );

  // Sync local values when settings prop changes (e.g., on initial load)
  useEffect(() => {
    setLocalValues({
      contingency_percent: settings.contingency_percent.toString(),
      contingency_fixed: settings.contingency_fixed
        ? (settings.contingency_fixed / 100).toString()
        : '',
      planning_overhead_percent: settings.planning_overhead_percent.toString(),
      planning_overhead_fixed: settings.planning_overhead_fixed
        ? (settings.planning_overhead_fixed / 100).toString()
        : '',
      profit_margin_percent: settings.profit_margin_percent.toString(),
      profit_margin_fixed: settings.profit_margin_fixed
        ? (settings.profit_margin_fixed / 100).toString()
        : '',
    });
    setContingencyMode(settings.contingency_fixed !== null ? 'fixed' : 'percent');
    setOverheadMode(settings.planning_overhead_fixed !== null ? 'fixed' : 'percent');
    setMarginMode(settings.profit_margin_fixed !== null ? 'fixed' : 'percent');
  }, [settings]);

  const handleContingencyModeChange = (mode: 'percent' | 'fixed') => {
    setContingencyMode(mode);
    if (mode === 'percent') {
      const percent = parseFloat(localValues.contingency_percent) || 0;
      onChange({ contingency_percent: percent, contingency_fixed: null });
    } else {
      const fixed = parseFloat(localValues.contingency_fixed) || 0;
      onChange({ contingency_percent: 0, contingency_fixed: Math.round(fixed * 100) });
    }
  };

  const handleContingencyChange = (value: string) => {
    setLocalValues(prev => ({ ...prev, contingency_percent: value }));
    if (contingencyMode === 'percent') {
      const numValue = parseFloat(value) || 0;
      onChange({ contingency_percent: numValue, contingency_fixed: null });
    }
  };

  const handleContingencyFixedChange = (value: string) => {
    setLocalValues(prev => ({ ...prev, contingency_fixed: value }));
    if (contingencyMode === 'fixed') {
      const numValue = parseFloat(value) || 0;
      onChange({ contingency_percent: 0, contingency_fixed: Math.round(numValue * 100) });
    }
  };

  const handleOverheadModeChange = (mode: 'percent' | 'fixed') => {
    setOverheadMode(mode);
    if (mode === 'percent') {
      const percent = parseFloat(localValues.planning_overhead_percent) || 0;
      onChange({
        planning_overhead_percent: percent,
        planning_overhead_fixed: null,
      });
    } else {
      const fixed = parseFloat(localValues.planning_overhead_fixed) || 0;
      onChange({
        planning_overhead_percent: 0,
        planning_overhead_fixed: Math.round(fixed * 100),
      });
    }
  };

  const handleOverheadPercentChange = (value: string) => {
    setLocalValues(prev => ({ ...prev, planning_overhead_percent: value }));
    if (overheadMode === 'percent') {
      const numValue = parseFloat(value) || 0;
      onChange({ planning_overhead_percent: numValue, planning_overhead_fixed: null });
    }
  };

  const handleOverheadFixedChange = (value: string) => {
    setLocalValues(prev => ({ ...prev, planning_overhead_fixed: value }));
    if (overheadMode === 'fixed') {
      const numValue = parseFloat(value) || 0;
      onChange({
        planning_overhead_percent: 0,
        planning_overhead_fixed: Math.round(numValue * 100),
      });
    }
  };

  const handleMarginModeChange = (mode: 'percent' | 'fixed') => {
    setMarginMode(mode);
    if (mode === 'percent') {
      const percent = parseFloat(localValues.profit_margin_percent) || 0;
      onChange({
        profit_margin_percent: percent,
        profit_margin_fixed: null,
      });
    } else {
      const fixed = parseFloat(localValues.profit_margin_fixed) || 0;
      onChange({
        profit_margin_percent: 0,
        profit_margin_fixed: Math.round(fixed * 100),
      });
    }
  };

  const handleMarginPercentChange = (value: string) => {
    setLocalValues(prev => ({ ...prev, profit_margin_percent: value }));
    if (marginMode === 'percent') {
      const numValue = parseFloat(value) || 0;
      onChange({ profit_margin_percent: numValue, profit_margin_fixed: null });
    }
  };

  const handleMarginFixedChange = (value: string) => {
    setLocalValues(prev => ({ ...prev, profit_margin_fixed: value }));
    if (marginMode === 'fixed') {
      const numValue = parseFloat(value) || 0;
      onChange({
        profit_margin_percent: 0,
        profit_margin_fixed: Math.round(numValue * 100),
      });
    }
  };

  // Toggle Button Group Component
  const ToggleGroup = ({
    value,
    onChange: onToggleChange,
    options
  }: {
    value: 'percent' | 'fixed';
    onChange: (value: 'percent' | 'fixed') => void;
    options: Array<{ value: 'percent' | 'fixed'; label: string }>;
  }) => (
    <div className="inline-flex rounded-md border border-gray-300 bg-white p-1" role="group">
      {options.map((option, index) => (
        <button
          key={option.value}
          type="button"
          onClick={() => onToggleChange(option.value)}
          className={cn(
            'px-3 py-1.5 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
            index === 0 && 'rounded-l-md',
            index === options.length - 1 && 'rounded-r-md',
            value === option.value
              ? 'bg-primary-600 text-white shadow-sm'
              : 'text-gray-700 hover:bg-gray-50'
          )}
          aria-pressed={value === option.value}
        >
          {option.label}
        </button>
      ))}
    </div>
  );

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {/* Contingency */}
      <div className="space-y-3">
        <div>
          <label className="block text-sm font-medium text-gray-900 mb-1">
            Contingency
          </label>
          <p className="text-xs text-gray-500">Buffer for unexpected costs</p>
        </div>
        <div className="flex items-center gap-3">
          <ToggleGroup
            value={contingencyMode}
            onChange={handleContingencyModeChange}
            options={[
              { value: 'percent', label: '%' },
              { value: 'fixed', label: currencyInfo.symbol },
            ]}
          />
          {contingencyMode === 'percent' ? (
            <Input
              type="number"
              min="0"
              max="100"
              step="0.5"
              value={localValues.contingency_percent}
              onChange={(e) => handleContingencyChange(e.target.value)}
              className="w-32 "
              leftIcon={<span className="text-sm text-gray-600 font-medium">%</span>}
              aria-label="Contingency percentage"
            />
          ) : (
            <Input
              type="number"
              min="0"
              step="0.01"
              placeholder="0.00"
              value={localValues.contingency_fixed}
              onChange={(e) => handleContingencyFixedChange(e.target.value)}
              className="w-32 "
              leftIcon={<span className="text-sm text-gray-600 font-medium">{currencyInfo.symbol}</span>}
              aria-label="Contingency fixed amount"
            />
          )}
        </div>
      </div>

      {/* Planning Overhead */}
      <div className="space-y-3">
        <div>
          <label className="block text-sm font-medium text-gray-900 mb-1">
            Planning Overhead
          </label>
          <p className="text-xs text-gray-500">Moniker professional services</p>
        </div>
        <div className="flex items-center gap-3">
          <ToggleGroup
            value={overheadMode}
            onChange={handleOverheadModeChange}
            options={[
              { value: 'percent', label: '%' },
              { value: 'fixed', label: currencyInfo.symbol },
            ]}
          />
          {overheadMode === 'percent' ? (
            <Input
              type="number"
              min="0"
              max="100"
              step="0.5"
              value={localValues.planning_overhead_percent}
              onChange={(e) => handleOverheadPercentChange(e.target.value)}
              className="w-32 "
              leftIcon={<span className="text-sm text-gray-600 font-medium">%</span>}
              aria-label="Planning overhead percentage"
            />
          ) : (
            <Input
              type="number"
              min="0"
              step="0.01"
              placeholder="0.00"
              value={localValues.planning_overhead_fixed}
              onChange={(e) => handleOverheadFixedChange(e.target.value)}
              className="w-32 "
              leftIcon={<span className="text-sm text-gray-600 font-medium">{currencyInfo.symbol}</span>}
              aria-label="Planning overhead fixed amount"
            />
          )}
        </div>
      </div>

      {/* Profit Margin */}
      <div className="space-y-3">
        <div>
          <label className="block text-sm font-medium text-gray-900 mb-1">
            Profit Margin
          </label>
          <p className="text-xs text-gray-500">Moniker margin on program</p>
        </div>
        <div className="flex items-center gap-3">
          <ToggleGroup
            value={marginMode}
            onChange={handleMarginModeChange}
            options={[
              { value: 'percent', label: '%' },
              { value: 'fixed', label: currencyInfo.symbol },
            ]}
          />
          {marginMode === 'percent' ? (
            <Input
              type="number"
              min="0"
              max="100"
              step="0.5"
              value={localValues.profit_margin_percent}
              onChange={(e) => handleMarginPercentChange(e.target.value)}
              className="w-32 "
              leftIcon={<span className="text-sm text-gray-600 font-medium">%</span>}
              aria-label="Profit margin percentage"
            />
          ) : (
            <Input
              type="number"
              min="0"
              step="0.01"
              placeholder="0.00"
              value={localValues.profit_margin_fixed}
              onChange={(e) => handleMarginFixedChange(e.target.value)}
              className="w-32 "
              leftIcon={<span className="text-sm text-gray-600 font-medium">{currencyInfo.symbol}</span>}
              aria-label="Profit margin fixed amount"
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default SettingsPanel;
